﻿import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, Car, Clock, Download, Edit, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { Customer } from "@/hooks/customers/useCustomerData";
import type { Vehicle } from "@/hooks/customers/useCustomerData";
import {
  
  
  handleExportCustomerData } from "@/hooks/customers/useCustomerData";

export function ExpandedCustomerRow({
  customer,
  metrics,
  custVehicles,
  setEditingCustomer,
  setAddingVehicleTo,
  setEditingVehicle,
  orders,
  vehicles,
}: {
  customer: Customer;
  metrics: any;
  custVehicles: Vehicle[];
  setEditingCustomer: (c: Customer) => void;
  setAddingVehicleTo: (id: string) => void;
  setEditingVehicle: (v: Vehicle) => void;
  orders: any[];
  vehicles: Vehicle[];
}) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-slate-900/50 border-t border-slate-800 p-6 overflow-hidden"
    >
      <div className="flex justify-end gap-2 mb-6 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="bg-emerald-950/20 text-emerald-400 border-emerald-900/50 hover:bg-emerald-900/40 hover:text-emerald-300"
          onClick={(e) => {
            e.stopPropagation();
            handleExportCustomerData(customer, vehicles, orders);
          }}
          title="Exportar dados do cliente para conformidade com a LGPD (Portabilidade)"
        >
          <Download size={14} className="mr-2" /> Exportar Dados (LGPD)
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-slate-800 border-slate-700 hover:bg-slate-700"
          onClick={(e) => {
            e.stopPropagation();
            setEditingCustomer(customer);
          }}
        >
          <Edit size={14} className="mr-2" /> Editar Cadastro
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-blue-900/30 text-blue-400 border-blue-900/50 hover:bg-blue-900/50 hover:text-blue-300"
          onClick={(e) => {
            e.stopPropagation();
            setAddingVehicleTo(customer.id);
          }}
        >
          <Plus size={14} className="mr-2" /> Adicionar Veículo
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
              <p className="text-slate-400 text-xs mb-1 uppercase font-bold tracking-wider">
                Total Gasto
              </p>
              <p className="text-2xl font-bold text-emerald-400">
                R$ {metrics.totalSpent.toFixed(2)}
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
              <p className="text-slate-400 text-xs mb-1 uppercase font-bold tracking-wider">
                Visitas
              </p>
              <p className="text-2xl font-bold text-blue-400">
                {metrics.visits}
              </p>
            </div>
            <div className="col-span-2 bg-slate-800 rounded-lg p-4 border border-slate-700 flex justify-between items-center">
              <div>
                <p className="text-slate-400 text-xs mb-1 uppercase font-bold tracking-wider">
                  Última Visita
                </p>
                <p className="font-semibold text-slate-200">
                  {metrics.lastVisit
                    ? format(metrics.lastVisit, "dd/MM/yyyy", { locale: ptBR })
                    : "Nunca"}
                </p>
              </div>
              <Calendar className="text-slate-600" size={24} />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <Car size={16} className="text-slate-500" /> Garagem (
              {custVehicles.length})
            </h4>
            <div className="space-y-2">
              {custVehicles.map((v) => (
                <div
                  key={v.id}
                  className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
                >
                  <div>
                    <p className="font-bold text-white uppercase">{v.plate}</p>
                    <p className="text-xs text-slate-400">
                      {v.model} • {v.color}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingVehicle(v);
                    }}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              ))}
              {custVehicles.length === 0 && (
                <p className="text-xs text-slate-500 italic">
                  Nenhum veículo cadastrado.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
            <Clock size={16} className="text-slate-500" /> Histórico Recente de
            Serviços
          </h4>
          <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
            {metrics.custOrders.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                Nenhuma ordem de serviço (PAGA) encontrada para este cliente.
              </div>
            ) : (
              <div className="divide-y divide-slate-700/50 max-h-[320px] overflow-y-auto">
                {metrics.custOrders.slice(0, 10).map((order: any) => (
                  <div
                    key={order.id}
                    className="p-4 hover:bg-slate-700/30 transition-colors flex justify-between items-center"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-200">
                        {order.services?.length > 0
                          ? order.services[0].name
                          : "Serviço Personalizado"}
                        {order.services?.length > 1 && (
                          <span className="text-slate-500 text-xs ml-1">
                            +{order.services.length - 1}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {format(
                          new Date(order.created_at),
                          "dd 'de' MMM, yyyy • HH:mm",
                          {
                            locale: ptBR,
                          },
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-400">
                        R$ {order.total.toFixed(2)}
                      </p>
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-emerald-900/30 text-emerald-400 rounded border border-emerald-900/50 mt-1 inline-block">
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
                {metrics.custOrders.length > 10 && (
                  <div className="p-3 text-center text-xs text-slate-500 bg-slate-800/80">
                    Exibindo as 10 visitas mais recentes de um total de{" "}
                    {metrics.custOrders.length}.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
